import { useRouter } from "next/router";
import React from "react";
import {
  BreadcrumbContainer,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbNav,
  BreadcrumbSeparator,
} from "./BreadcrumbComponent.style";

export interface BreadcrumbItem {
  label: string;
  url: string;
}

export interface BreadcrumbConfig {
  [path: string]: BreadcrumbItem[];
}

interface BreadcrumbComponentProps {
  className?: string;
  hostname?: string;
  customConfig?: BreadcrumbConfig;
}

// Default configuration for insurance pages
const defaultBreadcrumbConfig: BreadcrumbConfig = {
  // For www.makemytrip.com
  "/insurance": [{ label: "Home", url: "/insurance" }],
  "/insurance/listing": [
    { label: "Home", url: "/insurance" },
    { label: "Travel Insurance", url: "/insurance/listing" },
  ],
  "/insurance/traveller-details": [
    { label: "Home", url: "/insurance" },
    { label: "Traveler Details", url: "/insurance/traveller-details" },
  ],
  // For insurance.makemytrip.com (root level paths)
  "/": [{ label: "Home", url: "/insurance" }],
  "/listing": [
    { label: "Home", url: "/insurance" },
    { label: "Travel Insurance", url: "/insurance/listing" },
  ],
  "/traveller-details": [
    { label: "Home", url: "/insurance" },
    { label: "Traveler Details", url: "/insurance/traveller-details" },
  ],
};

const BreadcrumbComponent: React.FC<BreadcrumbComponentProps> = ({
  className,
  hostname: propHostname,
  customConfig,
}) => {
  const router = useRouter();

  // Get current path and hostname
  const currentPath = router.asPath;
  // Remove query parameters to get clean pathname
  const pathname = currentPath.split("?")[0];
  const hostname =
    propHostname ||
    (typeof window !== "undefined"
      ? window.location.hostname
      : "www.makemytrip.com");

  const generateBreadcrumbs = () => {
    const config = customConfig || defaultBreadcrumbConfig;

    // Find matching configuration for current path
    let breadcrumbItems: BreadcrumbItem[] = [];

    // Check for exact match first
    if (config[pathname]) {
      breadcrumbItems = config[pathname];
    } else {
      // Check for partial matches (for dynamic routes)
      for (const configPath in config) {
        if (pathname.startsWith(configPath) && configPath !== "/") {
          breadcrumbItems = config[configPath];
          break;
        }
      }
    }

    // If no match found, create default breadcrumb based on hostname
    if (breadcrumbItems.length === 0) {
      const isInsuranceSubdomain = hostname.includes(
        "insurance.makemytrip.com"
      );
      breadcrumbItems = [
        {
          label: "Home",
          url: isInsuranceSubdomain ? "/" : "/insurance",
        },
      ];
    }

    // Transform to internal breadcrumb format
    return breadcrumbItems.map((item, index) => {
      const isCurrentPage = index === breadcrumbItems.length - 1;
      return {
        label: item.label,
        href: isCurrentPage ? null : `https://${hostname}${item.url}`,
        isActive: isCurrentPage,
      };
    });
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumb if only home exists or if we're on home page
  const isInsuranceSubdomain = hostname.includes("insurance.makemytrip.com");
  const isHomePage = isInsuranceSubdomain
    ? pathname === "/"
    : pathname === "/insurance";

  if (breadcrumbs.length <= 1 && isHomePage) {
    return null;
  }

  return (
    <BreadcrumbNav aria-label="Breadcrumb" className={className}>
      <BreadcrumbContainer>
        {breadcrumbs.map((breadcrumb, index) => (
          <React.Fragment key={index}>
            <BreadcrumbItem
              isActive={breadcrumb.isActive}
              aria-current={breadcrumb.isActive ? "page" : undefined}
            >
              {breadcrumb.href ? (
                <BreadcrumbLink
                  href={breadcrumb.href}
                  onClick={(e) => {
                    e.preventDefault();
                    if (breadcrumb.href) {
                      window.location.href = breadcrumb.href;
                    }
                  }}
                >
                  {breadcrumb.label}
                </BreadcrumbLink>
              ) : (
                breadcrumb.label
              )}
            </BreadcrumbItem>
            {index < breadcrumbs.length - 1 && (
              <BreadcrumbSeparator aria-hidden="true">&gt;</BreadcrumbSeparator>
            )}
          </React.Fragment>
        ))}
      </BreadcrumbContainer>
    </BreadcrumbNav>
  );
};

export default BreadcrumbComponent;
